import { debounce } from "@solid-primitives/scheduled";
import { twJoin } from "tailwind-merge";
import { AirtablePromptBasePromise } from "~/js/network/airtable-prompt-base";
import { customStructuredClone } from "~/js/utilities/customStructuredClone";
import {
	getUserDreamboothModels,
	getUserLorasModels
} from "~/js/utilities/getUserDreamboothModels";
import { getUserStore } from "~/solidJs/entities/HeaderMain/models";
import { FileApi } from "~/solidJs/shared/api/fileApi";
import { isUserFree, onNextFrame, setDataAtrOnlyInTests } from "~/solidJs/shared/helpers";
import { scaleResolutionInBound } from "~/solidJs/shared/helpers/imageHelpers";
import {
	findSocket,
	preparePromptToFileName,
	serializeNodeOutputs
} from "~/solidJs/shared/helpers/nodeHelpers";
import { getImageDimensionsFromFile } from "~/solidJs/shared/helpers/validateImages";
import { openNodeContextMenu } from "~/solidJs/widgets/NodeContextMenu";
import { styleMethods } from "~/solidJs/widgets/NodeStyleWidget/NodeStyleWidget";
import AssetViewport, { MultiAssetViewport } from "../asset-viewport/asset-viewport";
import Editor from "../editor";
import { NodeDefinition, SocketDefinition } from "../nodes/node-definition";
import { NodeEvents, SocketEvents } from "../types/IEventorListners";
import EventAnalytics from "../utilities/EventAnalytics";
import Eventor from "../utilities/Eventor";
import { getMultiSocketName } from "../utilities/getMultiSocketName";
import { generate_UUID } from "../utilities/UUID";
import DataEntry from "./data-entry";
import {
	GroupLayout,
	getDataTypeColor,
	NodeLayout,
	SettingsGroupLayout,
	SocketLayout,
	SocketToggleGroupLayout
} from "./node-layout";
import { NodeLoadingLayout } from "./node-loading-layout";
import { NodeModifier } from "./nodeModifier";
import { SocketCustomErrorMessage } from "./socketCustomErrorMessage";

/**
 * @param {NodeDefinition}    definition
 * @param {x}   number
 * @param {y}   number
 */
export class GraphNode {
	/*
	 * @type boolean
	 */
	isStyleOpen = false;
	isModificationEnabled = true;
	inputSocketGroupModifiers = new NodeModifier();
	/**
	 * @type {import("../nodes/node-executor").NodeExecutor}
	 */
	executuionBlock;
	promptHelperSource = AirtablePromptBasePromise;
	eventor = new Eventor();
	needUpdate = false;
	isReExecutable = true;
	/**
	 * @type {ReturnType<typeof getUserDreamboothModels>}
	 */
	myModels;
	/**
	 * @type {ReturnType<typeof getUserLorasModels>}
	 */
	myLoras;
	toggleGroups = [];
	isLimited = false;
	/**
	 * @type {import("../nodes/node-definition").NodeDefinition}
	 */
	definition;

	/**
	 * @type ()=>void
	 */
	execute;
	/**
	 * @type {boolean}
	 */
	isStylesShowable = false;
	/**
	 * @type {number}
	 */
	taskID;
	/**
	 * @type {Record<string, any>}
	 */
	meta = {};
	/**
	 * @type {NodeLayout}
	 */
	layout;
	/**
	 *
	 * @param {NodeDefinition} definition
	 * @param {*} x
	 * @param {*} y
	 * @param {*} defaultParams
	 * @param {*} isLimitDisabled
	 */
	constructor(definition, x, y, defaultParams = true, isLimitDisabled = false) {
		// Reassign node params according to user's project
		this.isStylesShowable = definition.isStylesShowable;
		this.isModificationEnabled = definition.isModificationEnabled;
		this.layout = new NodeLayout(definition, this.redrawConnections.bind(this), this);
		this.layout.setWidth(definition.copiedWidth ? definition.copiedWidth : 350);
		this.loader = new NodeLoadingLayout();
		this.taskID = 0;
		this.definition = definition;
		this.userParams = [...(definition.params ? definition.params : [])];
		this.userInputs = [...(definition.inputs ? definition.inputs : [])];
		if ((definition.params || definition.inputs) && definition.userTemplateParams) {
			if (defaultParams) {
				for (let i = 0; i < definition.params.length; i++) {
					const defaultParam = definition.defaults.params.filter(
						(_defaultParam) => _defaultParam?.name === definition.params[i].name
					)[0];
					if (defaultParam) {
						definition.params[i].optionalInfo.value = defaultParam.optionalInfo.value;
						definition.params[i].optionalInfo.isFixedSeed =
							defaultParam.optionalInfo.isFixedSeed;
					}
				}
				for (let i = 0; i < definition.inputs.length; i++) {
					const defalutInput = definition.defaults.inputs.filter(
						(_defaultInput) => _defaultInput?.name === definition.inputs[i].name
					)[0];
					if (defalutInput) {
						definition.inputs[i].optionalInfo.value = defalutInput.optionalInfo.value;
					}
				}
			} else {
				if (definition.params && definition.userTemplateParams.params) {
					for (let i = 0; i < this.userParams.length; i++) {
						let user_param;
						if (Array.isArray(definition.userTemplateParams.params)) {
							user_param = definition.userTemplateParams.params.find(
								(user_param, _index) => user_param?.name === this.userParams[i].name
							);
						} else {
							user_param =
								definition.userTemplateParams.params[this.userParams[i].name];
						}

						if (user_param) {
							const optionalInfoClone = customStructuredClone(
								this.userParams[i].optionalInfo
							);
							if (
								user_param.optionalInfo &&
								typeof user_param.optionalInfo === "object"
							) {
								for (const key in user_param.optionalInfo) {
									optionalInfoClone[key] = user_param.optionalInfo[key];
								}
							}
							if (this.userParams[i].name === "artistic_mode") {
								user_param.optionalInfo?.valueOptions?.forEach((item, index) => {
									optionalInfoClone.valueOptions[index].checked = item.checked;
								});
							}
							optionalInfoClone.value = user_param.value;
							optionalInfoClone.isFixedSeed = user_param?.isFixedSeed;
							optionalInfoClone.isExpaned =
								user_param.optionalInfo?.isExpaned !== undefined
									? user_param.optionalInfo?.isExpaned
									: optionalInfoClone.isExpaned;

							this.userParams[i] = new SocketDefinition(
								definition.params[i].name,
								definition.params[i].dataType,
								optionalInfoClone,
								definition.params[i].optional,
								definition.params[i].displayName
							);
						}
					}
				}

				if (definition.inputs && definition.userTemplateParams.inputSocketGroup) {
					for (let i = 0; i < definition.inputs.length; i++) {
						let user_input;
						if (Array.isArray(definition.userTemplateParams.inputSocketGroup)) {
							user_input = definition.userTemplateParams.inputSocketGroup.find(
								(user_input, _index) => user_input?.name === this.userInputs[i].name
							);
						} else {
							user_input =
								definition.userTemplateParams.inputSocketGroup[
									this.userInputs[i].name
								];
						}
						if (user_input) {
							const optionalInfoClone = customStructuredClone(
								this.userInputs[i].optionalInfo
							);
							if (
								user_input?.optionalInfo !== null &&
								typeof user_input?.optionalInfo === "object"
							) {
								for (const key in user_input.optionalInfo) {
									optionalInfoClone[key] = user_input.optionalInfo[key];
								}
							}
							optionalInfoClone.value = user_input.value;
							if (user_input?.isFixedSeed) {
								optionalInfoClone.isFixedSeed = user_input.isFixedSeed;
							}
							this.userInputs[i] = new SocketDefinition(
								definition.inputs[i].name,
								definition.inputs[i].dataType,
								optionalInfoClone,
								definition.inputs[i].optional,
								definition.inputs[i].displayName
							);
						}
					}
				}
			}
		}

		if (definition.userTemplateParams?.meta) {
			if (!this.meta.prevOutputs && definition.userTemplateParams.outputSocketGroup) {
				this.meta.prevOutputs = definition.userTemplateParams.outputSocketGroup;
			}
			this.meta = definition.userTemplateParams.meta;
		}
		if (definition.userTemplateParams && !defaultParams) {
			this.uuid = definition.userTemplateParams.uuid || generate_UUID();
			this.taskID = definition.userTemplateParams.taskID;
			if (definition.userTemplateParams.position) {
				this.layout.setPosition(
					definition.userTemplateParams.position.x,
					definition.userTemplateParams.position.y
				);
			} else {
				const fixedPos = Editor.context.graph.navigation.clientToGraphSpace(x, y);
				this.layout.setPosition(fixedPos.x - 100, fixedPos.y - 100);
			}
			if (definition.userTemplateParams?.width) {
				this.layout.setWidth(definition.userTemplateParams.width);
			}
		} else if (definition?.copiedPosition) {
			this.uuid = generate_UUID();
			this.layout.setPosition(definition.copiedPosition.x, definition.copiedPosition.y);
		} else {
			const fixedPos = Editor.context.graph.navigation.clientToGraphSpace(x, y);
			this.uuid = generate_UUID();
			this.layout.setPosition(fixedPos.x - 100, fixedPos.y - 100);
		}

		this.name = definition.name;
		this.globalId = definition.globalId;
		this.serviceVersion = definition.serviceVersion;
		this.averageTimeInSeconds = definition.averageTimeInSeconds;
		this.userTemplateParams = definition.userTemplateParams;
		if (definition.isNeedToFetchLoras) {
			this.getLorasModels();
		}
		this.outputSocketGroup = new OutputSocketGroup(
			this,
			"Outputs",
			definition.copiedOutputs && definition.copiedOutputs.length > 0
				? definition.copiedOutputs
				: definition.outputs,
			definition.name,
			definition.multiSocket,
			definition.isNeedToInitViewer,
			true,
			false
		);

		if (definition.useOutputViewer && !definition.isNeedToInitViewer)
			this.outputSocketGroup.initViewer();
		this.inputSocketGroup = new SocketGroup(
			this,
			"Inputs",
			this.userInputs.length > 0 ? this.userInputs : definition.inputs,
			"",
			false,
			true,
			false
		);
		for (const input of this.inputSocketGroup.getSockets()) {
			input.eventor.addListener("ga-change", () =>
				EventAnalytics.changeInputValue(this.globalId, input.name)
			);
		}
		this.paramSocketGroups = new SocketSubGroups(
			this,
			"General",
			this.userParams.length > 0 ? this.userParams : definition.params
		);
		for (const input of this.paramSocketGroups.getSockets()) {
			input.eventor.addListener("ga-change", () =>
				EventAnalytics.changeInputValue(this.globalId, input.name)
			);
		}

		this.triggerSocketsIn = [];
		this.triggerSocketsOut = [];
		this.executionGroupLayout = new GroupLayout();
		definition.initExecutionGroup(this, this.executionGroupLayout);

		const paramSocketGroupsLayoutElements = [];
		for (const socketGroup of this.paramSocketGroups.socketGroups) {
			if (socketGroup.sockets.length > 0) {
				paramSocketGroupsLayoutElements.push(socketGroup.layout.domElement());
			}
		}

		this.layout.appendContent(
			this.outputSocketGroup.layout.domElement(),
			this.inputSocketGroup.layout.domElement()
		);
		this.paramSocketGroups.globalSettingsGroup &&
			this.layout.appendContent(
				this.paramSocketGroups.globalSettingsGroup.layout.domElement()
			);
		if (
			this.inputSocketGroup.movedToSettings.length > 0 ||
			paramSocketGroupsLayoutElements.length > 0
		) {
			this.initSettingsGroup([
				...this.inputSocketGroup.movedToSettings,
				...paramSocketGroupsLayoutElements
			]);
		}

		this.layout.DOM.nodExecutorContainer.prepend(this.executionGroupLayout.domElement());

		this.layout.appendLoader(this.loader.domElement());

		this.getSocketsIn = () => this.inputSocketGroup.getSockets();
		this.getSocketsParam = () => this.paramSocketGroups.getSockets();
		this.getAllSocketsIn = () => [
			...this.inputSocketGroup.getSockets(),
			...this.paramSocketGroups.getSockets(),
			...this.triggerSocketsIn
		];
		this.getSocketsOut = () => [...this.outputSocketGroup.getSockets()];
		/**
		 *
		 * @returns {SocketIn[]}
		 */
		this.getAllSocketsOut = () => [
			...this.outputSocketGroup.getSockets(),
			...this.triggerSocketsOut
		];
		this.getAllSockets = () => [...this.getAllSocketsIn(), ...this.getAllSocketsOut()];
		const rerenderConnections = debounce(() => {
			for (const socket of this.getAllSockets()) {
				for (const connection of socket.connections) {
					connection.redrawConnection(true);
				}
			}
		}, 20);
		this.layout.addListener("resize", () => {
			onNextFrame(rerenderConnections);
		});

		this.layout.DOM.close.addEventListener("click", () => {
			this.delete();
		});

		// Add double click handler for new context menu
		this.layout.domElement().addEventListener("contextmenu", (event) => {
			event.preventDefault();
			event.stopPropagation();

			openNodeContextMenu(this.uuid, {
				left: event.clientX,
				top: event.clientY
			});
		});

		// Old context menu replaced with new NodeContextMenu (activated by double click)
		// const itemGroups = [
		// 	new ContextMenuItem("Delete", () => this.delete()),
		// 	new ContextMenuItem("Clone", async () => {
		// 		const copied = await Editor.context.graph.serializeNodesAndThierConnections(
		// 			[this],
		// 			false,
		// 			true
		// 		);
		// 		Editor.context.graph.pasteNodes(copied);
		// 	})
		// ];
		// this.contextMenu = new ContextMenu(this.layout.domElement(), "Node", itemGroups);

		this.toggleLimitDisability(isLimitDisabled);
		this.initToggleGroups();
	}

	getTaskPrice() {
		return (
			this.meta.taskPrice || {
				price: this.definition.defaultPrice,
				details: { base_price: this.definition.defaultPrice, params: [], inputs: [] }
			}
		);
	}
	setTaskPrice(price) {
		this.meta.taskPrice = price;
	}

	initToggleGroups() {
		for (const group of this.toggleGroups) {
			group.toggleSockets(true);
		}
	}
	getAdditionalParamsForTask(_jsonData) {}
	/**
	 *
	 * @returns {Promise<IAirtablePromptBase>}
	 */
	get getPromptHelperSource() {
		return this.promptHelperSource;
	}
	getHasResults() {
		for (const out of this.getSocketsOut() || []) {
			if (out.tryGetValue()) {
				return true;
			}
		}
		return false;
	}
	updateBtnText() {
		this?.executuionBlock?.updateGenerateText?.();
	}
	/**
	 *
	 * @param source {Promise<IAirtablePromptBase> | IAirtablePromptBase}
	 */
	set setPromptHelperSource(source) {
		this.promptHelperSource = source;
		const textPrompt = this.inputSocketGroup.socketHashMap.get("text_prompt");
		if (!textPrompt) return;
		textPrompt?.dataEntry?.genPrompt?.generatePrompt("randomizePrompt");
	}

	closeStyleWidget() {
		this.isStyleOpen = false;
		styleMethods.deleteItem({
			nodeUUID: this.uuid
		});
	}

	remove() {
		for (const viewPort of this.outputSocketGroup.assetViewports) {
			viewPort.remove();
		}
		this.eventor.invoke(NodeEvents.Remove);
	}

	setSelect(isSelected) {
		this.selected = isSelected;
		this.layout.setSelectionStyle(isSelected);
		this.eventor.invoke(NodeEvents.NodeSelected, isSelected);
	}

	createGhost() {
		const ghost = document.createElement("div");
		ghost.style.position = "absolute";
		ghost.className = twJoin("rounded-xl border border-blue-500");
		ghost.style.pointerEvents = "none"; // Crucial
		ghost.style.backgroundColor = "rgba(0, 150, 255, 0.1)";
		ghost.style.width = `${this.layout.getNodeWidth()}px`;
		ghost.style.height = `${this.layout.getHeight()}px`;
		return ghost;
	}

	getAllOutConnectedNodes() {
		const connectedNodes = [];
		const getedNodeIds = {};
		for (const socket of this.outputSocketGroup.getSockets()) {
			for (const connection of socket.connections) {
				if (connection.socketIn.node.uuid in getedNodeIds) continue;
				getedNodeIds[connection.socketIn.node.uuid] = true;
				connectedNodes.push(connection.socketIn.node);
			}
		}
		return connectedNodes;
	}
	getAllConnectedNodes() {
		const connectedNodes = [];
		const getedNodeIds = {};
		for (const socket of this.outputSocketGroup.getSockets()) {
			for (const connection of socket.connections) {
				if (connection.socketIn.node.uuid in getedNodeIds) continue;
				getedNodeIds[connection.socketIn.node.uuid] = true;
				connectedNodes.push(connection.socketIn.node);
			}
		}
		for (const socket of this.inputSocketGroup.getSockets()) {
			for (const connection of socket.connections) {
				if (connection.socketOut.node.uuid in getedNodeIds) continue;
				getedNodeIds[connection.socketOut.node.uuid] = true;
				connectedNodes.push(connection.socketOut.node);
			}
		}
		return connectedNodes;
	}
	setRelatedToComment(commentId) {
		this.relatedCommentId = commentId;
	}
	/**
	 *
	 * @param {{
	 * 	requestedValue: number
	 * 	displayName: string;
	 * 	name: string;
	 * }} socketInfo
	 * @description used for openning premium node modal
	 */
	togglePremiumIntBanner(socketInfo) {
		this.layout.showPremiumIntBanner(socketInfo);
	}
	/**
	 *
	 * @returns {Connection[]}
	 */
	getAllConnections() {
		const connections = [];
		for (const socket of this.outputSocketGroup.getSockets()) {
			for (const connection of socket.connections) {
				connections.push(connection);
			}
		}
		for (const socket of this.inputSocketGroup.getSockets()) {
			for (const connection of socket.connections) {
				connections.push(connection);
			}
		}
		return connections;
	}

	/**
	 *
	 */
	redrawConnections() {
		for (const connection of this.getAllConnections()) {
			connection.redrawConnection();
		}
	}
	/**
	 *
	 * @param {SocketIn} socketIn
	 * @param {boolean} isInputToFile
	 */
	async getSerializeValues(socketIn, isInputToFile) {
		let value;
		if (!isInputToFile || socketIn.dataType === "text") {
			return await socketIn.dataEntry.getValue();
		} else {
			value = await socketIn.tryGetValue();
			if (Array.isArray(value)) {
				if (value.length === 0) {
					return socketIn.getOriginalWorkspaceIds();
				}
				for (let i = 0; i < value.length; i++) {
					if (!value[i]) continue;
					const id = await FileApi.syncFileWithStorageAsync(value[i]);
					value[i] = {
						fileId: id,
						name: value[i].name.split("__")[0].split(".")[0]
					};
				}
			} else {
				if (!value) return socketIn.getOriginalWorkspaceIds();
				const id = await FileApi.syncFileWithStorageAsync(value);
				value = [
					{
						fileId: id,
						name: value.name.split("__")[0].split(".")[0]
					}
				];
			}
		}
	}

	/**
	 *
	 * @param {boolean} isNeedToCopyConnections - if true, will save connection info
	 * @param {boolean} isNeedToCopyOutputs - if true, will sync files data with backend (used for workspace saving)
	 * @param {boolean} isInputToFile - if true will sync not only outputs, but also inputs of the node
	 * @param {boolean} isFixedSeed - if true will forcly fix seed for all params in node (deprecated, was used in workspace)
	 * @returns {IUserTemplateParams}
	 */
	async serializeNodeState(
		isNeedToCopyConnections = true,
		isNeedToCopyOutputs = true,
		isInputToFile = false,
		isFixedSeed = false
	) {
		this.eventor.invoke(NodeEvents.PreSavePreparing);
		const nodeData = {
			globalId: this.globalId,
			name: this.name,
			id: this.serviceId,
			uuid: this.uuid,
			taskID: this.taskID,
			serviceVersion: this.serviceVersion,
			inputSocketGroup: {},
			outputSocketGroup: [],
			meta: { ...this.meta },
			params: {},
			width: 250,
			position: { x: "", y: "" },
			...(isNeedToCopyConnections ? { connections: [] } : {})
		};
		nodeData.position = this.layout.getPosition();
		nodeData.width = this.layout.getNodeWidth();
		nodeData.height = this.layout.getHeight();
		for (const socketIn of this.inputSocketGroup.sockets) {
			const value = await this.getSerializeValues(socketIn, isInputToFile);

			nodeData.inputSocketGroup[socketIn.name] = {
				name: socketIn.name,
				type: socketIn.dataType,
				value,
				isFixedSeed: socketIn.optionalInfo.isFixedSeed,
				optionalInfo: {
					isEnabled: socketIn.isEnabled,
					mapOfEnabylity: socketIn.mapOfEnabylity,
					...(socketIn.dataType === "object3d" ? socketIn.optionalInfo : {}),
					...(await socketIn.customSerializationData(isNeedToCopyOutputs)),
					originalWorkspaceIds: value
				}
			};
		}

		for (const paramSocket of this.paramSocketGroups.getSockets()) {
			const value = await paramSocket.getValue();
			const objToSave = {
				name: paramSocket.name,
				type: paramSocket.dataType,
				optionalInfo: {
					isEnabled: paramSocket.isEnabled,
					mapOfEnabylity: paramSocket.mapOfEnabylity,
					...(await paramSocket.customSerializationData(isNeedToCopyOutputs))
				},
				value,
				isFixedSeed: isFixedSeed ? true : paramSocket.optionalInfo.isFixedSeed
			};
			if (paramSocket.name === "artistic_mode") {
				objToSave.optionalInfo.valueOptions = paramSocket.optionalInfo.valueOptions.map(
					(item) => ({ checked: item.checked })
				);
			}

			objToSave.optionalInfo.isExpaned = paramSocket.optionalInfo?.isExpaned;
			nodeData.params[paramSocket.name] = objToSave;
		}
		if (isNeedToCopyOutputs && this.definition.isNeedToCopyOutputs) {
			let nodeValues = await serializeNodeOutputs(this);
			if (!nodeValues?.length) {
				nodeValues = this.meta.prevOutputs;
			}
			if (nodeValues?.length) {
				nodeData.outputSocketGroup = nodeValues;
				this.meta.prevOutputs = nodeValues;
				nodeData.meta = { ...this.meta };
			}
		}

		if (!isNeedToCopyConnections) return nodeData;

		for (const socket of this.getAllSocketsOut()) {
			for (const connection of socket.connections) {
				nodeData.connections.push({
					from: {
						name: connection.socketOut.name,
						type: connection.socketOut.dataType,
						nodeUuid: connection.socketOut.node.uuid
					},
					to: {
						name: connection.socketIn.name,
						type: connection.socketIn.dataType,
						nodeUuid: connection.socketIn.node.uuid
					}
				});
			}
		}
		return nodeData;
	}

	async setMockData() {
		const fileUrl = "/public/Success_1.5.png";
		const response = await fetch(fileUrl);
		const blob = await response.blob();
		const filename = fileUrl.split("/").pop();
		const file = new File([blob], filename, { type: blob.type });
		const data = [
			{
				name: "final_image",
				type: "image",
				value: [file]
			}
		];

		this.updateOutputs(data);
	}

	getDreamboothModels() {
		this.myModels = getUserDreamboothModels(this.globalId);
	}
	getLorasModels() {
		this.myLoras = getUserLorasModels(this.globalId);
	}
	initSettingsGroup(paramSocketGroupsLayoutElements) {
		this.settingsGroup = new SettingsGroup("Settings");
		this.settingsGroup.layout.getDomBodyContainer().append(...paramSocketGroupsLayoutElements);
		this.layout.appendContent(this.settingsGroup.layout.getDomElement());

		this.isSettingsGroupInited = true;
	}

	addListener = (eventName, listener) => this.eventor.addListener(eventName, listener);

	removeListener = (eventName, listener) => this.eventor.removeListener(eventName, listener);

	/**
	 *
	 * @param {boolean} isLimited
	 */
	toggleLimitDisability(isLimited) {
		this.isLimited = isLimited;
		if (this.executuionBlock) {
			const user = getUserStore();
			this.executuionBlock.toggleDisability(
				isLimited,
				user?.become_available_at,
				isUserFree(user)
			);
		}
	}

	/**
	 * @description update existing sockets and asset assetViewports or create new sockets and init new assetViewport
	 * @param newOutputs {ITaskSocketDownloaded[]}
	 */
	updateOutputs(newOutputs, invokeUpdate = true) {
		const nodeOutputs = this.outputSocketGroup.getSockets();
		let isNeedToInvoke = true;
		let outNameIdx = 0;
		const optionalAssetName = getPromptAndSeedAsString(this);
		serviceOutputLoop: for (const serviceOutput of newOutputs) {
			// If service returns a list of values
			if (Array.isArray(serviceOutput.value)) {
				const _nameIdx = 0;

				if (serviceOutput.type === "text") {
					for (const nodeOutput of nodeOutputs) {
						if (nodeOutput.name === serviceOutput.name) {
							nodeOutput.value = serviceOutput.value[0];
						}
					}
					continue;
				}
				isNeedToInvoke = false;
				for (const serviceOutValue of serviceOutput.value) {
					serviceOutValue.optionalName = optionalAssetName;
					if (outNameIdx < nodeOutputs.length) {
						this.outputSocketGroup.assetViewports[outNameIdx].loadAssetAsync(
							serviceOutValue
						);
						this.outputSocketGroup.sockets[outNameIdx].layout.setTitle(outNameIdx + 1);
						this.outputSocketGroup.sockets[outNameIdx].setDisplayName(outNameIdx + 1);
						this.outputSocketGroup.sockets[outNameIdx].fireChange();
						this.outputSocketGroup.sockets[outNameIdx].value = serviceOutValue;
						this.outputSocketGroup.sockets[outNameIdx].dataType =
							serviceOutValue.type.split("/")[0];

						outNameIdx++;
						continue;
					}
					const newSocket = new SocketDefinition(
						// serviceOutput.name,
						`${serviceOutput.name}__${outNameIdx}`,
						serviceOutValue.type.split("/")[0],
						{ value: serviceOutValue },
						null
					);

					newSocket.setDisplayName(outNameIdx + 1);
					this.outputSocketGroup.addSockets([newSocket]);

					this.outputSocketGroup.addAndInitViewerWithValue(
						serviceOutValue,
						newSocket.name
					);
					// this.migrateConnectionFromArray(outNameIdx);
					outNameIdx++;
				}
				if (serviceOutput.value.length < this.outputSocketGroup.sockets.length) {
					const i = serviceOutput.value.length;
					while (i < this.outputSocketGroup.sockets.length) {
						this.outputSocketGroup.deleteSocketByName(
							this.outputSocketGroup.sockets[i].name
						);
						this.outputSocketGroup.deleteAssetViewPotByIndex(i);
					}
				}
			}
			// If service returns a single object
			else {
				for (const nodeOutput of nodeOutputs) {
					if (serviceOutput.type !== "text") {
						if ("value" in serviceOutput && optionalAssetName) {
							serviceOutput.value.optionalName = optionalAssetName;
						}
					} else if (nodeOutput.name === serviceOutput.name) {
						isNeedToInvoke = false;
						nodeOutput.value = serviceOutput.value;
						this.outputSocketGroup.assetViewports[0].loadAssetAsync(nodeOutput);
						continue serviceOutputLoop;
					}
					if (nodeOutput.name === serviceOutput.name) {
						isNeedToInvoke = false;
						nodeOutput.value = serviceOutput.value;
						this.outputSocketGroup.assetViewports[0].loadAssetAsync(
							serviceOutput.value
						);
					}
				}
			}
			outNameIdx++;
		}

		this.isReExecutable = false;
		if (isNeedToInvoke) {
			this.eventor.invoke(NodeEvents.OutputsChangeViewer);
		}
		if (invokeUpdate) {
			this.sayToConnectedAboutOutputUpdates();
			this.eventor.invoke("outputs-change");
		}
		this.updateBtnText();
		this.outputSocketGroup.updateShortSocketsToNewLayout();
	}
	migrateConnectionFromArray() {
		const prevSockets = this.getSocketsOut();
		if (prevSockets.length <= 0) return;
		const prevFirstSocket = prevSockets[0];
		if (!prevFirstSocket) return;
		const newSocket = prevSockets[prevSockets.length - 1];
		if (!newSocket) return;
		if (prevFirstSocket.definition.dataType !== "array") return;

		for (const connectionToMigrate of prevFirstSocket.connections) {
			connectionToMigrate.socketOut = newSocket;
			newSocket.connections.push(connectionToMigrate);
		}
		prevFirstSocket.connections = [];
	}
	clearAllOuputs() {
		this.eventor.invoke(NodeEvents.OutputsChangeViewer);
		this.eventor.clearListenersByEventName("outputs-change");
		this.clearOutputs();
		for (const viewer of this.outputSocketGroup.assetViewports) {
			viewer.clear();
		}
		this.outputSocketGroup.layout.deleteViewers();
		this.outputSocketGroup.removeViewports();
		this.outputSocketGroup.deleteAllSockets();
	}

	updateOutputByIndex(outputValue, index) {
		const outputs = this.outputSocketGroup.getSockets();
		if (outputs[index] !== undefined) outputs[index].value = outputValue;

		this.isReExecutable = false;
		this.sayToConnectedAboutOutputUpdates();
		this.eventor.invoke("outputs-change");
		this.eventor.invoke(NodeEvents.OutputsChangeViewer);
	}

	clearOutputs() {
		for (const outputSocket of this.outputSocketGroup.getSockets()) {
			outputSocket.value = null;
		}
		this.sayToConnectedAboutOutputUpdates();
		this.eventor.invoke("outputs-change");
		this.eventor.invoke(NodeEvents.OutputsChangeViewer);
	}

	sayToConnectedAboutOutputUpdates() {
		for (const socketsOut of this.outputSocketGroup.getSockets()) {
			socketsOut.fireChange();
		}
	}

	delete() {
		Editor.context.graph.deleteNode(this);
	}

	getIsModificationEnabled() {
		return this.isModificationEnabled;
	}

	setIsModificationEnabled(force) {
		this.isModificationEnabled = force;
	}

	setIsIslandLoaded(isIslandLoaded) {
		this.isIslandLoaded = isIslandLoaded;
	}
}

class SettingsGroup {
	constructor(title) {
		this.layout = new SettingsGroupLayout(title);
	}
}

/**
 * @param {string}    title     ...
 * @param {SocketDefinition[]}   socketsDefinitions   ...
 * @param {boolean}   isOutput   ...
 */
export class SocketGroup {
	/**
	 * @type SocketIn[]
	 */
	sockets = [];
	/**
	 * @type {Map<string, SocketIn>}
	 */
	socketHashMap = new Map();
	movedToSettings = [];
	constructor(
		node,
		title,
		socketsDefinitions,
		name,
		multiSocket = false,
		isAlwaysExpanded = false,
		isNeedToAddHeader = true
	) {
		this.node = node;
		this.title = title;
		this.layout = new GroupLayout(
			this.title,
			name,
			multiSocket,
			this.sockets,
			isAlwaysExpanded,
			isNeedToAddHeader
		);
		this.layout.setVisibility(false);

		if (socketsDefinitions) {
			this.setSockets(socketsDefinitions);
		}
	}

	getSockets = () => this.sockets;

	setSockets(definitions) {
		if (!definitions || definitions.length === 0) {
			this.deleteAllSockets();
			return;
		}
		const weHaveSockets = this.sockets.length > 0;
		const checkIfCanBeReplaced = () => {
			if (definitions.length !== this.sockets.length) return false;

			for (let i = 0; i < this.sockets.length; i++) {
				if (this.sockets[i].dataType !== definitions[i].dataType) return false;
			}
			return true;
		};

		if (weHaveSockets && checkIfCanBeReplaced()) {
			for (let i = 0; i < this.sockets.length; i++) {
				this.changeSocketName(this.sockets[i].name, definitions[i].name);
				this.sockets[i].replaceSocket(definitions[i]);
			}
		} else {
			this.deleteAllSockets();
			this.addSockets(definitions);
		}
		this.layout.sockets = [...this.sockets];
		this.layout.recalculateCollapsed();
	}

	addSockets(definitions) {
		if (!definitions || definitions.length === 0) return;
		this.layout.setVisibility(true);
		for (const definition of definitions) {
			const socket = this.getSocketInstance(definition);
			if (
				socket.name !== "style" &&
				socket.dataType === "modifier" &&
				definition.optionalInfo?.value
			) {
				socket.setValue(definition.optionalInfo.value[0]);
			}

			this.sockets.push(socket);
			this.socketHashMap.set(definition.name, socket);
			if (definition.optionalInfo.isNeedToMoveToSettings) {
				this.movedToSettings.push(socket.layout.domElement());
				if (socket.toggleGroup) {
					this.movedToSettings.push(socket.toggleGroup.getDomElement());
				}
			} else {
				if (socket.toggleGroup) {
					this.layout.appendSocket(socket.toggleGroup.getDomElement());
				}
				this.layout.appendSocket(socket.layout.domElement());
			}
		}
	}

	deleteAllSockets() {
		for (const socket of this.sockets) {
			socket.delete();
		}
		this.socketHashMap.clear();
		this.sockets = [];
		this.movedToSettings = [];
		this.layout.setVisibility(false);
	}

	deleteSocketByName(name) {
		const socket = this.socketHashMap.get(name);
		if (!socket) return;

		socket.delete();
		this.socketHashMap.delete(name);
		this.sockets = this.sockets.filter((socket) => socket.name !== name);

		if (this.sockets.length === 0) {
			this.layout.setVisibility(false);
		}
	}
	/**
	 *
	 * @param {string} oldName
	 * @param {string} newName
	 */
	changeSocketName(oldName, newName) {
		const oldSocket = this.socketHashMap.get(oldName);
		this.socketHashMap.delete(oldName);
		if (oldSocket) {
			this.socketHashMap.set(newName, oldSocket);
		}
	}

	getSocketInstance(definition) {
		return new SocketIn(definition, this.node);
	}
}

class SocketSubGroups {
	/*
	 * @type {SocketGroup[]}
	 */
	socketGroups = [];
	globalSettingsGroup;

	constructor(node, mainSubGroupTitle, socketDefinitions) {
		this.node = node;
		this.mainSubGroupTitle = mainSubGroupTitle;

		if (socketDefinitions) {
			this.divideIntoGroups(socketDefinitions);
			this.initSocketGroups();
		} else {
			const socketGroup = new SocketGroup(this.node, this.mainSubGroupTitle, []);
			this.socketGroups.push(socketGroup);
		}
	}
	/**
	 * @description find socket from sub group if exist
	 * @param {string} socketName
	 * @returns
	 */
	getSocketFromSubGroup(socketName) {
		for (const group of this.socketGroups) {
			if (group.socketHashMap.has(socketName)) {
				return group.socketHashMap.get(socketName);
			}
		}
	}
	divideIntoGroups(socketDefinitions) {
		this.groups = {};
		this.groups[this.mainSubGroupTitle] = [];
		this.globalSettingsLevelScokets = [];

		for (const socketDefinition of socketDefinitions) {
			if (socketDefinition.optionalInfo.isNeedToMoveFromSettings) {
				this.globalSettingsLevelScokets.push(socketDefinition);
				continue;
			}
			const groupName = socketDefinition.optionalInfo.group;

			if (!groupName) {
				this.groups[this.mainSubGroupTitle].push(socketDefinition);
				continue;
			}

			if (!this.groups[groupName]) this.groups[groupName] = [];

			this.groups[groupName].push(socketDefinition);
		}
	}

	initSocketGroups() {
		for (const [groupName, sockets] of Object.entries(this.groups)) {
			const socketGroup = new SocketGroup(
				this.node,
				groupName,
				sockets,
				groupName,
				false,
				groupName === this.mainSubGroupTitle
			);
			this.socketGroups.push(socketGroup);
		}
		if (this.globalSettingsLevelScokets.length > 0) {
			this.globalSettingsGroup = new SocketGroup(
				this.node,
				"groupName",
				this.globalSettingsLevelScokets,
				"",
				false,
				true,
				false
			);
		}
	}

	getSockets() {
		let sockets = [];
		for (const socketGroup of this.socketGroups) {
			sockets = [...sockets, ...socketGroup.getSockets()];
		}
		if (this.globalSettingsGroup) {
			sockets = [...this.globalSettingsGroup.getSockets(), ...sockets];
		}
		return sockets;
	}
}
/**
 *
 * @param {GraphNode} node
 * @returns
 */
function getPromptAndSeedAsString(node) {
	let promptAndSeed = "";
	if (node.globalId === "Phygital Creator/phygc-rnd-upscale") {
		const inputSocket = node.inputSocketGroup.socketHashMap.get("img_input");
		const val = inputSocket?.tryGetValue();
		if (val) {
			promptAndSeed += `${val?.optionalName || val?.name} Upscaled`;
			return promptAndSeed;
		}
	}
	if (node.inputSocketGroup) {
		const textPromptSocket =
			node.inputSocketGroup.socketHashMap.get("text_prompt") ||
			node.inputSocketGroup.socketHashMap.get("description");
		if (textPromptSocket) {
			let prompt = String(textPromptSocket.tryGetValue());
			prompt = preparePromptToFileName(prompt);
			promptAndSeed = prompt;
		}

		let seedSocket;
		for (const group of node.paramSocketGroups.socketGroups) {
			if (group.socketHashMap.has("seed")) {
				seedSocket = group.socketHashMap.get("seed");
				break;
			}
		}

		if (seedSocket) {
			promptAndSeed = `${promptAndSeed}_${String(seedSocket.tryGetValue())}`;
		}
	}
	if (!promptAndSeed) {
		promptAndSeed += node.name;
	}
	return promptAndSeed;
}

class OutputSocketGroup extends SocketGroup {
	constructor(
		node = {},
		title = "",
		socketsDefinitions = {},
		name = "",
		multiSocket = false,
		isNeedToInitViewer = false,
		isAlwaysExpanded = false,
		isNeedToAddHeader = true
	) {
		super(
			node,
			title,
			socketsDefinitions,
			name,
			multiSocket,
			isAlwaysExpanded,
			isNeedToAddHeader
		);

		this.assetViewports = [];
		if (isNeedToInitViewer) {
			if (this.node.definition.multiViewer) {
				this.sockets.length > 0
					? this.addAndInitViewerWithValue(this.sockets[0]?.value)
					: this.initViewer();
			} else {
				for (const socket of this.sockets) {
					if (socket.dataType === "text") {
						socket?.value
							? this.addAndInitViewerWithValue(socket, socket.name)
							: this.initViewer();
					} else {
						socket?.value
							? this.addAndInitViewerWithValue(socket.value, socket.name)
							: this.initViewer();
					}
				}
				this.updateShortSocketsToNewLayout();
			}
		}
		this.multiSocket = multiSocket;
		multiSocket && this.updateMultiSockets();
	}

	updateShortSocketsToNewLayout() {
		this.layout.toggleNewSocketLayout(
			this.sockets.every((socket) => {
				return (socket.getDisplayName()?.toString()?.length || 4) <= 3;
			})
		);
	}

	recalculateNewSockets() {
		if (this.node.definition.multiViewer) {
			this.assetViewports[0].loadAssetAsync(this.sockets);
			this.sockets.forEach((socket, index) => {
				if (socket.clickHandler) {
					socket.layout.domElement().removeEventListener("click", socket.clickHandler);
				}
				const setActiveViewporHandler = () => this.assetViewports[0].setActiveIndex(index);
				socket.clickHandler = setActiveViewporHandler;
				socket.layout.domElement().addEventListener("click", setActiveViewporHandler);
			});
		}
		if (this.sockets.length === 0) {
			this.assetViewports.forEach((s) => {
				s.clear();
			});
		}
		if (this.multiSocket) {
			this.updateMultiSockets();
		}
	}

	async initViewer() {
		if (!this.node.definition.multiViewer && !this.sockets.length) return;
		this.useViewer = true;
		this.assetViewports = [
			this.node.definition.multiViewer
				? new MultiAssetViewport(null, this.sockets)
				: new AssetViewport(null, this.sockets[0], this.sockets[0].name)
		];
		// this.layout.appendContent(this.assetViewports[0].domElement)
		this.layout.appendViewer(this.assetViewports[0].domElement);

		const updateViewer = () => {
			if (this.node.definition.multiViewer) {
				this.assetViewports[0].loadAssetAsync(this.sockets);
			} else {
				if (this.sockets[0].dataType === "text") {
					this.assetViewports[0].loadAssetAsync(this.sockets[0]);
					this.assetViewports[0].viewer.addOutputListener();
				} else {
					this.assetViewports[0].loadAssetAsync(this.sockets[0].value);
				}
			}
		};
		this.node.addListener(NodeEvents.OutputsChangeViewer, updateViewer);
	}

	deleteAssetViewPotByIndex(index) {
		this.assetViewports[index].remove();
		this.assetViewports.splice(index, 1);
	}

	addAndInitViewerWithValue(value, socketName) {
		// TODO: Refactor this method?
		const updateViewer = () => {
			if (this.node.definition.multiViewer) {
				this.assetViewports[0].loadAssetAsync(this.sockets);
			} else {
				if (this.sockets[0].dataType === "text") {
					this.assetViewports[0].loadAssetAsync(this.sockets[0]);
					this.assetViewports[0].viewer.addOutputListener();
				} else {
					this.assetViewports[0].loadAssetAsync(this.sockets[0].value);
				}
			}
		};
		if (this.node.definition.multiViewer) {
			this.layout.deleteViewers();
			this.assetViewports = [new MultiAssetViewport(this.sockets, this, socketName)];
			this.layout.appendViewer(this.assetViewports[0].domElement);
			this.node.addListener(NodeEvents.OutputsChangeViewer, updateViewer);
		} else {
			const assetViewport = new AssetViewport(value, this.sockets[0], socketName);
			this.assetViewports.push(assetViewport);
			assetViewport.setTitle(this.assetViewports.length);
			const lastViewerIdx = this.assetViewports.length - 1;

			if (value.type === "text") {
				assetViewport.viewer.addOutputListener();
			}

			this.layout.appendViewer(this.assetViewports[lastViewerIdx].domElement);
		}
	}

	removeViewports() {
		this.assetViewports = [];
	}

	setSockets(definitions) {
		super.setSockets(definitions);
		if (!this.useViewer) return;

		if (this.sockets.length === 0) {
			this.assetViewport.clear();
		}
	}

	addSockets(definitions) {
		super.addSockets(definitions);

		if (this.node.definition.multiViewer) {
			this.sockets.forEach((socket, index) => {
				const setActiveViewporHandler = () => this.assetViewports[0].setActiveIndex(index);
				socket.clickHandler = setActiveViewporHandler;
				socket.layout.domElement().addEventListener("click", setActiveViewporHandler);
			});
		}
	}

	deleteAllSockets() {
		super.deleteAllSockets();
	}

	/**
	 * reduce provided sockets to sockets dataType, create multy socket for each dataTyp
	 */
	updateMultiSockets() {
		const socketNameAndType = [];
		const hashMap = new Map();
		for (const socket of this.sockets) {
			const previousValue = hashMap.get(socket.dataType);
			if (previousValue) {
				hashMap.set(socket.dataType, previousValue + 1);
				continue;
			}
			hashMap.set(socket.dataType, 1);
		}
		for (const [key, value] of hashMap) {
			value > 1 &&
				socketNameAndType.push({
					name: getMultiSocketName(key, value),
					type: key
				});
		}

		this.addMultiSockets(socketNameAndType);
	}

	addMultiSockets(socketsNameAndType) {
		if (!socketsNameAndType) return;
		this.layout.setVisibility(true);
		const arrayOfDoms = [];
		// TODO: optimize amount of operations with DOM
		for (const nameAndType of socketsNameAndType) {
			// if(this.socketsHash.has(nameAndType.type))continue
			// this.socketsHash.set(nameAndType.type, 'random')

			const socket = this.getSocketInstance(
				new SocketDefinition(nameAndType.name, nameAndType.type),
				true
			);

			// this.sockets.push(socket)
			arrayOfDoms.push(socket.layout.domElement());
		}
		this.layout.replaceMultiSockets(arrayOfDoms);
	}

	getSocketInstance(definition, multiSocket) {
		return multiSocket
			? new MultiSocketOut(definition, this.node, this.name)
			: new SocketOut(definition, this.node, this);
	}

	getViewportBySocketName(socketName) {
		if (!this.assetViewports || this.assetViewports.length === 0) {
			return null;
		}

		return this.assetViewports.find((v) => v.activeSocketName === socketName) || null;
	}
}

export class Socket {
	/**
	 * @type {GraphNode}
	 */
	node;
	eventor = new Eventor();
	/**
	 * @type {Connection[]}
	 */
	connections = [];
	/**
	 * @type {IDataType}
	 */
	dataType;

	/**
	 * @type {SocketCustomErrorMessage}
	 */
	errorMessage;
	isEnabled = true;
	/**
	 * @param {SocketDefinition}    definition     ...
	 * @param {GraphNode}   node   ...
	 */
	constructor(definition, node) {
		this.node = node;
		this.setup(definition);
		this.layout = new SocketLayout(definition, this);
		this.initToggleGroup();
		this.toggleLayoutEnabled(this.isEnabled);
		this.isEnabled = true;
		this.mapOfEnabylity = {};
		this.isHandleHidden = definition.optionalInfo?.isHandleHidden || false;
		this.isBeingDragged = false;
		this.initAbilityToConnect();
		this.errorMessage = new SocketCustomErrorMessage(
			definition.name,
			this.getDataType(),
			definition.getName(),
			definition?.optionalInfo?.customErrorMessages
		);
	}
	getDisplayName() {
		return this.displayName || this.definition.displayName || this.name;
	}
	/**
	 * @returns {IDataType} underlying data type of a socket
	 */
	getDataType() {
		let out = this.dataType;
		if (out === "array") {
			out = this.options.itemType.dataType;
		}
		return out;
	}
	/**
	 *
	 * @param {SocketCustomErrorMessageType} type
	 * @returns {string | undefined} custom error message if it exists
	 */
	getErrorMessage(type) {
		return this.errorMessage?.getErrorMessage(type);
	}

	/**
	 *
	 * @param {string} name
	 */
	setDisplayName(name) {
		this.displayName = name;
	}

	fireChange() {
		this.eventor.invoke(SocketEvents.Change);
		this.node.eventor.invoke(NodeEvents.InputsChange);
	}

	reactiveChange() {
		this.eventor.invoke(SocketEvents.Change);
	}

	getIsModificationEnabled() {
		return this.node.getIsModificationEnabled();
	}

	setup(definition) {
		this.definition = definition;
		this.name = definition.name;
		this.dataType = definition.dataType;
		this.optional = definition.optional;
		this.accessDisabled = definition.optionalInfo.accessDisabled;
		if (definition.optionalInfo.isEnabled !== undefined) {
			this.isEnabled = definition.optionalInfo.isEnabled;
		}
		if (definition.optionalInfo.mapOfEnabylity !== undefined) {
			this.mapOfEnabylity = definition.optionalInfo.mapOfEnabylity;
		}
		this.value = definition.optionalInfo.value;
		this.options = definition.optionalInfo.valueOptions;
		this.optionalInfo = customStructuredClone(definition.optionalInfo);
	}
	initToggleGroup() {
		if (!this.optionalInfo.toggleGroup) return;
		this.toggleGroup = new SocketToggleGroupLayout(this.optionalInfo.toggleGroup, this.node);
		this.node.toggleGroups.push(this.toggleGroup);
	}

	setEnabled(isEnabled, socketNameOFModify, isNeedTogleGroup = true) {
		const prevEnable = this.isEnabled;
		const newEnable = isEnabled;
		if (prevEnable !== newEnable) {
			if (isEnabled) {
				delete this.mapOfEnabylity[socketNameOFModify];
				if (Object.keys(this.mapOfEnabylity).length > 0) {
					isEnabled = false;
				}
			} else {
				this.mapOfEnabylity[socketNameOFModify] = isEnabled;
			}
			this.isEnabled = isEnabled;
			if (isNeedTogleGroup && this.toggleGroup) {
				this.toggleGroup.toggleSockets(isEnabled);
			}

			this.toggleLayoutEnabled(isEnabled);
			return prevEnable !== isEnabled;
		}
		return false;
	}

	toggleLayoutEnabled(isEnabled) {
		this.layout.setEnabled(isEnabled);
		this.connections.forEach((connection) => {
			connection.setEnabled(isEnabled);
		});
	}

	setOptional(isOptional) {
		const isChanged = this.Modifiedoptional !== isOptional;
		this.Modifiedoptional = isOptional;

		return isChanged;
	}

	lowDepthChange() {
		this.eventor.invoke("lowDepthChange");
	}

	customSerializationData(_isNeedToSynch) {
		return {};
	}

	async modifyDm(props) {
		let image = await this.tryGetValue();
		if (Array.isArray(image)) {
			image = image[0];
		}
		if (!image) return;
		const heightSocket = findSocket(props.heightSocket, this.node);
		const widthSocket = findSocket(props.widthSocket, this.node);

		const dm = await getImageDimensionsFromFile(image);
		if (!dm || !heightSocket || !widthSocket) return;

		if (dm.height === dm.width && dm.height < 1024) {
			heightSocket.setValue(1024);
			widthSocket.setValue(1024);
		} else {
			const { height, width } = scaleResolutionInBound(
				dm,
				{
					height: heightSocket.getMin(),
					width: widthSocket.getMin()
				},
				{
					height: heightSocket.getMax(),
					width: widthSocket.getMax()
				}
			);
			heightSocket.setValue(height);
			widthSocket.setValue(width);
		}
	}

	modifyValue(props) {
		const { modification, toSocketName, nodeSocket, enabledValueModification } = props;
		const value = modification.value;
		if (!value) return false;
		if (modification.hidden) {
			this.node.inputSocketGroupModifiers.addModification(this.name, {
				fromSocketName: toSocketName,
				value: value
			});
			return false;
		}
		if (enabledValueModification && "value" in modification) {
			const oldValue = nodeSocket.valueToBoolean();
			if (value === "random") {
				nodeSocket.setValue(Math.floor(Math.random() * 10000000));
			} else nodeSocket.setValue(value);
			const newValue = nodeSocket.valueToBoolean();
			return oldValue !== newValue;
		}

		return false;
	}

	async modifySockets(modifiers, isModificationWasEnabled) {
		if (!modifiers) return;

		const enabledValueModification =
			(isModificationWasEnabled === undefined ? true : isModificationWasEnabled) &&
			this.getIsModificationEnabled();
		if (enabledValueModification && "modifyRes" in modifiers && this.dataType === "image") {
			const image = await this.tryGetValue();
			const dm = await getImageDimensionsFromFile(image);
			const resolutionSocket = findSocket(modifiers.modifyRes.resolutionSocket, this.node);
			if (dm.height === dm.width) {
				if (resolutionSocket) {
					resolutionSocket.setValue(modifiers.modifyRes.value);
				}
			}
		}
		if (enabledValueModification && "modifyDm" in modifiers && this.getDataType() === "image") {
			await this.modifyDm(modifiers.modifyDm);
		}
		for (const toSocketName in modifiers) {
			const nodeSocket = findSocket(toSocketName, this.node);
			if (!nodeSocket) {
				continue;
			}
			const modification = modifiers[toSocketName];
			let isChanged = false;
			if ("options" in modification) {
				nodeSocket.replaceOptions(modification.options);
			}
			isChanged = this.modifyValue({
				enabledValueModification,
				modification,
				toSocketName,
				nodeSocket
			});
			if ("isEnabled" in modification) {
				const isHasBeenChanged = nodeSocket.setEnabled(modification.isEnabled, this.name);
				if (isHasBeenChanged) {
					isChanged = true;
				}
			}
			if ("isOptional" in modification) {
				nodeSocket.setOptional(modification.isOptional);
			}

			isChanged && nodeSocket.lowDepthChange();
		}
	}

	replaceSocket(definition) {
		this.layout.setTitle(definition.getName());
		if (definition.optionalInfo.valueOptions) definition.optionalInfo.valueOptions = undefined;

		this.setup(definition);
	}
	initAbilityToConnect() {
		let tmpConnection = null;

		const onMouseLeaveDueConnection = () => {
			tmpConnection.setTargetSocket(null);
		};

		const onMouseEnterDueConnection = () => {
			tmpConnection.setTargetSocket(this);
		};

		this.setConnectAbilityDueConnection = (isConnectable, connection) => {
			this.layout.setConnectable(isConnectable);

			if (!isConnectable) return;
			tmpConnection = connection;
			this.layout.addSnapEventListeners(onMouseEnterDueConnection, onMouseLeaveDueConnection);
		};

		this.resetConnectAbilityDueConnection = () => {
			this.layout.clearAbilityToConnect();
			this.layout.removeSnapEventListeners(
				onMouseEnterDueConnection,
				onMouseLeaveDueConnection
			);
			tmpConnection = null;
		};
	}
	setConnected(isConnected) {
		this.layout.setConnected(isConnected);
		this.isConnected = isConnected;
		if (isConnected) {
			this.eventor.invoke(SocketEvents.AddConnection);
			return;
		}
		this.eventor.invoke(SocketEvents.RemoveConnection);
	}
	addListener = (eventName, listener) => this.eventor.addListener(eventName, listener);

	removeListener = (eventName, listener) => this.eventor.removeListener(eventName, listener);

	/**
	 * Check if this socket can connect to the target socket
	 * @param {Socket} targetSocket - The socket to check compatibility with
	 * @returns {boolean} - True if connection is possible
	 */
	canConnectTo(targetSocket) {
		// Check if sockets are of opposite types (in vs out)
		const hasOppositeSocketInstance = this.type !== targetSocket.type;
		if (!hasOppositeSocketInstance) return false;

		// Check data type compatibility
		const socketTargetFileType = targetSocket.getDataType();
		const socketCurrentFileType = this.getDataType();

		let hasSuitableType = socketCurrentFileType.includes(socketTargetFileType);

		// Special case for image_svg
		if (socketCurrentFileType === "image_svg") {
			hasSuitableType = false;
		}

		return hasSuitableType;
	}

	delete() {
		const len = this.connections.length;
		for (let i = 0; i < len; i++) {
			this.connections[this.connections.length - 1].delete();
		}
		this.layout.delete();
	}
}

/**
 * @param {SocketDefinition}    definition     ...
 * @param {GraphNode}   node   ...
 */
export class SocketIn extends Socket {
	constructor(definition, node) {
		super(definition, node);
		this.type = "in";
		this.initConnectionWay();
		this.initDataEntry();
		this.addGlobalModifiersListeners();
		this.optional = definition.optional ? definition.optional : null;
	}
	async onConnectedCallback() {
		this.tempPrompt = this.dataEntry.getValue();
		if (this.dataType !== "text") return;
		const newTextValue = await this.connections[0].socketOut.tryGetValue();
		if (!newTextValue) return;
		this.dataEntry.setValue(newTextValue);
	}
	async onDeConnectedCallback() {
		if (this.dataType !== "text") return;
		this.dataEntry.setValue(this.tempPrompt || "");
	}

	replaceOptions(options) {
		this.dataEntry.replaceOptions(options);
	}

	getMax() {
		return this.options.max;
	}

	getMin() {
		return this.options.min;
	}

	getOriginalWorkspaceIds() {
		return this.optionalInfo?.originalWorkspaceIds || [];
	}

	addGlobalModifiersListeners() {
		const modifierFcChecked = (isModificateonWasEnabled) => {
			const booleanValue = this.valueToBoolean();
			if (!booleanValue || booleanValue === this.prevBooleanValue) return;
			this.prevBooleanValue = booleanValue;
			if (!this.optionalInfo.globalCheckedModifiers) return;
			this.modifySockets(
				this.optionalInfo.globalCheckedModifiers?.params,
				isModificateonWasEnabled
			);
			this.modifySockets(
				this.optionalInfo.globalCheckedModifiers?.inputs,
				isModificateonWasEnabled
			);
		};
		const modifierFcUnChecked = (isModificateonWasEnabled) => {
			const booleanValue = this.valueToBoolean();
			if (booleanValue || booleanValue === this.prevBooleanValue) return;
			this.prevBooleanValue = booleanValue;
			if (!this.optionalInfo.globalUnCheckedModifiers) return;
			this.modifySockets(
				this.optionalInfo.globalUnCheckedModifiers?.params,
				isModificateonWasEnabled
			);
			this.modifySockets(
				this.optionalInfo.globalUnCheckedModifiers?.inputs,
				isModificateonWasEnabled
			);
		};
		this.eventor.addListener(SocketEvents.Change, modifierFcChecked);
		this.eventor.addListener(SocketEvents.ConnectionChange, modifierFcChecked);
		this.eventor.addListener("lowDepthChange", modifierFcChecked);
		setTimeout(() => modifierFcChecked(this.getIsModificationEnabled()), 0);

		this.eventor.addListener(SocketEvents.Change, modifierFcUnChecked);
		this.eventor.addListener(SocketEvents.ConnectionChange, modifierFcUnChecked);
		this.eventor.addListener("lowDepthChange", modifierFcUnChecked);
		setTimeout(() => modifierFcUnChecked(this.getIsModificationEnabled()), 0);
	}

	initConnectionWay() {
		if (this.isHandleHidden) return; // Don't initialize if handle is hidden
		this.layout.getConnector().addEventListener("mousedown", (mouseEvent) => {
			if (mouseEvent.which !== 1) return;

			if (this.connections.length > 0) {
				const connection = this.connections[0];

				connection.delete();
			} else {
				new Connection(this, null);
			}
		});
	}

	initDataEntry() {
		this.dataEntry = new DataEntry(
			this.dataType,
			this.value,
			this.options,
			this.node,
			this.name,
			this.optionalInfo,
			this
		);
		this.dataEntry.addListener("change", (value) => {
			return this.eventor.invoke("change", value);
		});
		if (this.dataEntry.domElement()) {
			this.layout.setDataEntry(
				this.dataEntry.domElement(),
				this.dataEntry.dom.isFixedSeed,
				this.dataEntry.getDomElementName()
			);
			this.dataEntry.addListener("ga-change", () => {
				this.node.eventor.invoke(NodeEvents.InputsChange);
				this.node.eventor.invoke(NodeEvents.ParamsChange);
				this.eventor.invoke("ga-change");
			});

			if (this.dataEntry.dom.isFixedSeed) {
				this.dataEntry.dom.isFixedSeed.addEventListener("change", (_e) => {
					this.node.eventor.invoke(NodeEvents.InputsChange);
					this.node.eventor.invoke(NodeEvents.ParamsChange);
					if ((!"isFixedSeed") in this.optionalInfo) {
						this.optionalInfo.isFixedSeed = true;
					} else {
						this.optionalInfo.isFixedSeed = !this.optionalInfo.isFixedSeed;
					}
				});
			}
		}
	}
	valueHasBeenChanged() {
		this.fireChange();
		this.node.isReExecutable = true;
		// this.node.sayToConnectedAboutOutputUpdates();
	}
	valueFromConHasBeenChanged() {
		this.eventor.invoke(SocketEvents.ConnectionChange);
	}
	valueToBoolean() {
		if (!this.isEnabled) return false;
		if (this.connections.length > 0) {
			if (this.connections.length === 1) return !!this.connections[0].tryGetValue();
			for (const connection of this.connections) {
				const value = connection.tryGetValue();
				if (value) {
					return true;
				}
			}
			return false;
			// return this.connections[0].tryGetValue();
		}
		return !!this.dataEntry.getValue();
	}

	replaceSocket(definition) {
		super.replaceSocket(definition);

		this.initDataEntry();
	}

	getValue = async () => {
		if (this.dataType === "array") {
			// if (this.connections.length > 0) {
			// if (this.connections.length == 1)
			//     return await this.connections[0].getValue()

			const values = [];
			for (const connection of this.connections) {
				values.push(await connection.getValue());
			}
			return values;
			// return await this.connections[0].getValue();
		}
		if (this.connections.length === 1) return this.connections[0].getValue();
		return this.dataEntry.getValue();
	};

	tryGetValue = () => {
		if (this.connections.length > 0) {
			if (this.connections.length === 1) return this.connections[0].tryGetValue();

			const values = [];
			for (const connection of this.connections) values.push(connection.tryGetValue());
			return values;
			// return this.connections[0].tryGetValue();
		}
		return this.dataEntry.getValue();
	};

	setValue = async (value) => {
		return this.dataEntry.setValue(value);
	};
}

/**
 * @param {SocketDefinition}    definition     ...
 * @param {GraphNode}   node   ...
 */
export class SocketOut extends Socket {
	constructor(definition, node, group) {
		super(definition, node);
		this.type = "out";
		this.initConnectionWay();
		this.layout.markAsOutput();
		if (new.target === SocketOut && this.node.definition.isNeedToAddClearBtn) {
			this.layout.addRemoveBtn();
		}
		this.group = group;
	}

	getOriginalWorkspaceIds() {
		return this.optionalInfo.originalWorkspaceIds;
	}

	fireChange() {
		this.eventor.invoke(SocketEvents.Change);
		this.node.eventor.invoke(NodeEvents.OutputsChange);
	}
	initConnectionWay() {
		if (this.isHandleHidden) return; // Don't initialize if handle is hidden
		this.layout.getConnector().addEventListener("mousedown", (mouseEvent) => {
			if (mouseEvent.which !== 1) return;
			this.isBeingDragged = true;
			this.node.outputSocketGroup.getViewportBySocketName(this.name)?.highlight();
			new Connection(null, this); // This connection will handle unhighlighting
		});
	}

	async getValue() {
		if (!this.value) {
			if (this.node.execute !== undefined) {
				await this.node.execute();
			}
		}
		return this.value;
	}

	tryGetValue() {
		return this.value;
	}
}

export class MultiSocketOut extends SocketOut {
	/**
	 * Override canConnectTo for MultiSocketOut - only allow connection to array type sockets
	 * @param {Socket} targetSocket - The socket to check compatibility with
	 * @returns {boolean} - True if connection is possible
	 */
	canConnectTo(targetSocket) {
		// Check if sockets are of opposite types (in vs out)
		const hasOppositeSocketInstance = this.type !== targetSocket.type;
		if (!hasOppositeSocketInstance) return false;

		// MultiSocket can only connect to array type sockets
		return targetSocket.dataType === "array";
	}

	initConnectionWay() {
		this.layout.getConnector().addEventListener("mousedown", (mouseEvent) => {
			if (mouseEvent.which !== 1) return;

			const connectAllOutputs = (socketIn) => {
				if (!socketIn) return;
				const arrOfSockets = this.node.getAllSocketsOut();
				for (const socketOut of arrOfSockets) {
					if (socketOut.dataType === this.dataType) {
						let isContinue = false;
						if (socketOut.connections && socketOut.connections.length > 0) {
							for (const previousConnection of socketOut.connections) {
								if (previousConnection.socketIn === socketIn) {
									isContinue = true;
									break;
								}
							}
						}
						if (isContinue) continue;
						new Connection(socketIn, socketOut, true, false, null, true);
					}
				}
				socketIn.valueHasBeenChanged();
			};
			const layout = this.layout;

			function onMouseUpCallback() {
				if (!this.getTargetSocket()) return;
				layout.showLoadingIcon(true);

				setTimeout(() => {
					this.delete();
					connectAllOutputs(this.getTargetSocket());

					setTimeout(() => {
						layout.showLoadingIcon(false);
					}, 0);
				}, 20);
			}

			new Connection(null, this, false, false, onMouseUpCallback);
		});
	}
}

/**
 * Node Input Output (variant of Socket Object)
 * @param {SocketIn}    socketIn     ...
 * @param {SocketOut}   socketOut   ...
 */
export class Connection {
	/**
	 * @type {SocketIn|SocketOut}
	 */
	socketOut;
	/**
	 * @type {SocketIn|SocketOut}
	 */
	socketIn;

	svgMemo = new SvgRedrawMemo();
	/**
	 * @description if isDoubleRedraw is true, then the connection is between two moving nodes, and we only need to redraw the connection position
	 */
	isDoubleRedraw = false;
	/**
	 * @description if isInRedrawList is true, then the connection already in rerender list, and we dont need to rerender its again'
	 */
	isInRedrawList = false;

	constructor(
		socketIn,
		socketOut,
		autoConnect = false,
		avoidAnalytics = false,
		onMouseUpCallback = () => {},
		multiConnect = false
	) {
		this.avoidAnalytics = avoidAnalytics;
		this.socketIn = socketIn;
		this.socketOut = socketOut;
		this.currentMouseEvent = false;
		this.updateGraphPositionInterval = -1;
		this.onMouseUpCallback = onMouseUpCallback;
		this.multiConnect = multiConnect;

		this.getValue = async () => {
			return await this.socketOut.getValue();
		};
		this.tryGetValue = () => {
			return this.socketOut.tryGetValue();
		};

		this.connectableSockets = [];

		if (socketIn) {
			this.getOriginSocket = () => this.socketIn;
			this.getTargetSocket = () => this.socketOut;
			this.setTargetSocket = (socket) => {
				this.socketOut = socket;
			};
		} else {
			this.getOriginSocket = () => this.socketOut;
			this.getTargetSocket = () => this.socketIn;
			this.setTargetSocket = (socket) => {
				this.socketIn = socket;
			};
		}

		this.svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
		setDataAtrOnlyInTests(() => {
			this.svg.setAttribute("data-test-from", socketOut?.name);
			this.svg.setAttribute("data-test-to", socketIn?.name);
		});
		this.path = document.createElementNS("http://www.w3.org/2000/svg", "path");
		this.svg.classList.add("connector-svg");
		this.svg.append(this.path);
		this.svg.style.stroke = getDataTypeColor(this.getOriginSocket().getDataType());

		this.path.addEventListener("click", () => {
			this.delete();
		});

		this.path.addEventListener("mouseenter", () => {
			if (this.socketOut) {
				this.socketOut.node.outputSocketGroup
					.getViewportBySocketName(this.socketOut.name)
					?.highlight();
			}
		});

		this.path.addEventListener("mouseleave", () => {
			if (this.socketOut) {
				this.socketOut.node.outputSocketGroup
					.getViewportBySocketName(this.socketOut.name)
					?.unhighlight();
			}
		});

		Editor.context.graph.domElement().append(this.svg);

		if (!multiConnect) {
			this.begin();
		}

		if (autoConnect) this.end();
	}

	setIsShown(isShown) {
		this.svg.classList.toggle("hidden", !isShown);
	}

	setEnabled(isEnabled) {
		this.svg.classList.toggle("disabled", !isEnabled);
	}

	begin() {
		if (!this.avoidAnalytics) {
			EventAnalytics.tryAddConnection(this.socketOut?.node?.globalId);
		}

		this.getOriginSocket().layout.setConnected(true);
		this.node = this.getOriginSocket().node;

		const setSocketConnectAbility = (socket, isConnectable) => {
			socket.setConnectAbilityDueConnection(isConnectable, this);
			if (isConnectable) this.connectableSockets.push(socket);
		};

		const classifyAllSocketsByConnectAbility = () => {
			for (const node of Editor.context.graph.nodes) {
				if (node === this.node) {
					for (const socket of node.getAllSockets()) {
						setSocketConnectAbility(socket, false);
					}
					continue;
				}

				// TODO учесть рекурсию

				for (const socket of node.getAllSockets()) {
					const currentSocket = this.getOriginSocket();
					const canConnect = currentSocket.canConnectTo(socket);
					setSocketConnectAbility(socket, canConnect);
				}
			}
		};

		classifyAllSocketsByConnectAbility();
		this.updateGraphPositionInterval = setInterval(
			() =>
				Editor.context.graph.navigation.moveGraphByDrag(
					this.currentMouseEvent.pageX,
					this.currentMouseEvent.pageY
				),
			5
		);
		this.onMouseMove = (mouseEvent) => {
			this.currentMouseEvent = mouseEvent;
			this.drawConnection(mouseEvent);
		};

		this.onMouseUp = () => {
			clearInterval(this.updateGraphPositionInterval);
			this.end();
			this.onMouseUpCallback?.();
		};
		if (this.socketIn) {
			this.socketIn.layout.updateConnectorPos(this.socketIn.node.layout.getPosition());
		}
		if (this.socketOut) {
			this.socketOut.layout.updateConnectorPos(this.socketOut.node.layout.getPosition());
		}
		this.currentMouseEvent = new Event("mousemove");
		this.onMouseMove(this.currentMouseEvent);
		this.svg.classList.add("begin");
		window.addEventListener("mousemove", this.onMouseMove);
		window.addEventListener("mouseup", this.onMouseUp);
	}

	end() {
		this.svg.classList.remove("begin");
		this.svg.style.zIndex = 0;
		window.removeEventListener("mousemove", this.onMouseMove);
		window.removeEventListener("mouseup", this.onMouseUp);

		// Unhighlight viewport for socketOut as the drag operation ends
		if (this.socketOut) {
			this.socketOut.isBeingDragged = false;
			this.socketOut.node.outputSocketGroup
				.getViewportBySocketName(this.socketOut.name)
				?.unhighlight();
		}

		if (!this.multiConnect) {
			for (const node of Editor.context.graph.nodes) {
				for (const socket of node.getAllSockets()) {
					socket.resetConnectAbilityDueConnection();
				}
			}
		} else {
			this.redrawConnection();
		}

		if (this.socketOut && this.socketIn) {
			setDataAtrOnlyInTests(() => {
				this.svg.setAttribute("data-test-from", this.socketOut?.name);
				this.svg.setAttribute("data-test-to", this.socketIn?.name);
			});
			if (this.socketIn.dataType !== "array") {
				for (const socketInConnections of this.socketIn.connections) {
					socketInConnections.delete();
				}
			}

			this.socketIn.connections.push(this);
			this.socketOut.connections.push(this);
			this.socketIn.setConnected(true);
			this.socketOut.setConnected(true);
			this.socketIn.onConnectedCallback();
			this.socketIn.valueHasBeenChanged();
			this.changeHandler = () => {
				this.onSocketOutChange();
			};
			this.socketOut.addListener(SocketEvents.Change, this.changeHandler);
			if (!this.avoidAnalytics)
				EventAnalytics.addConnection(
					this.socketOut?.node?.globalId,
					this.socketIn?.node?.globalId
				);
		} else {
			this.svg.remove();
			this.getOriginSocket().setConnected(false);
		}
	}

	onSocketOutChange = debounce(() => this.socketIn.valueFromConHasBeenChanged(), 200);

	drawConnection(mouseEvent) {
		const cursorPosition = Editor.context.graph.navigation.clientToGraphSpace(
			mouseEvent.clientX,
			mouseEvent.clientY
		);
		const start = this.socketIn
			? this.socketIn.layout.getConnectorPos(this.socketIn.node.layout.getPosition())
			: cursorPosition;
		const end = this.socketOut
			? this.socketOut.layout.getConnectorPos(this.socketOut.node.layout.getPosition())
			: cursorPosition;
		this.svgMemo.generateSvgPathConnection(
			this.svg,
			this.path,
			start.x,
			start.y,
			end.x,
			end.y,
			false
		);
	}
	/**
	 *
	 * @param {boolean} isInList
	 * @description set connection property {@link Connection.isInRedrawList}
	 */
	toggleAddedToRedrawList(isInList) {
		this.isInRedrawList = isInList;
	}
	/**
	 *
	 * @returns {boolean} connection property {@link Connection.isInRedrawList}
	 */
	getIsInRedrawList() {
		return this.isInRedrawList;
	}
	/**
	 * @description set connection property {@link Connection.isDoubleRedraw}
	 * @param {boolean} isDoubleRedraw
	 */
	setIsDoubleRedraw(isDoubleRedraw) {
		this.isDoubleRedraw = isDoubleRedraw;
	}
	/**
	 * @returns connection property {@link Connection.isDoubleRedraw}
	 */
	getIsDoubleRedraw() {
		return this.isDoubleRedraw;
	}

	/**
	 *
	 * @param {boolean} isNeedToUpdatePos
	 */
	redrawConnection(isNeedToUpdatePos = false) {
		const start = this.socketIn.layout.getConnectorPos(
			this.socketIn.node.layout.getPosition(),
			isNeedToUpdatePos
		);
		const end = this.socketOut.layout.getConnectorPos(
			this.socketOut.node.layout.getPosition(),
			isNeedToUpdatePos
		);

		this.svgMemo.generateSvgPathConnection(
			this.svg,
			this.path,
			start.x,
			start.y,
			end.x,
			end.y,
			this.getIsDoubleRedraw()
		);
	}

	delete(multiRemove = false) {
		this.svg.remove();

		if (!multiRemove) {
			const indexIn = this.socketIn.connections.indexOf(this);
			if (indexIn > -1) {
				this.socketIn.connections.splice(indexIn, 1);
			}
			const indexOut = this.socketOut.connections.indexOf(this);
			if (indexOut > -1) {
				this.socketOut.connections.splice(indexOut, 1);
			}

			if (this.socketIn.connections.length === 0) {
				this.socketIn.setConnected(false);
			}
			this.socketIn.onDeConnectedCallback();
			if (this.socketOut.connections.length === 0) {
				this.socketOut.setConnected(false);
			}

			this.socketIn.valueHasBeenChanged();
		}
		this.socketOut.removeListener(SocketEvents.Change, this.changeHandler);
		if (!this.avoidAnalytics)
			EventAnalytics.deleteConnection(
				this.socketOut?.node?.globalId,
				this.socketIn?.node?.globalId
			);
	}
}
/**
 * @description used to optimize connection redrawing logic
 */
class SvgRedrawMemo {
	/**
	 * Node Input Output (variant of Socket Object)
	 * @param {HTMLElement | SVGSVGElement} svg ...
	 * @param {HTMLElement | SVGPathElement} path ...
	 * @param {number}         x1   ...
	 * @param {number}         x2     ...
	 * @param {number}         y1     ...
	 * @param {number}         y2     ...
	 * @param {boolean}         isDoubleRedraw used to determine is need to rerender path atribute
	 * @description accept position in graph coordinates
	 */
	generateSvgPathConnection(svg, path, x1, y1, x2, y2, isDoubleRedraw) {
		const left = Math.min(x1, x2);
		const top = Math.min(y1, y2);

		svg.style.transform = `translate(${left}px, ${top}px)`;

		if (isDoubleRedraw) return;

		const width = Math.max(1, Math.abs(x1 - x2));
		const height = Math.max(1, Math.abs(y1 - y2));

		const curving = width / height;

		const off = Math.abs(x2 - x1);
		const level = off / (0.5 + curving);

		const c1x = x1 - level;
		const c1y = y1;
		const c2x = x2 + level;
		const c2y = y2;
		const d = `M ${x1 - left},${y1 - top} C ${c1x - left},${c1y - top} ${c2x - left},${
			c2y - top
		} ${x2 - left},${y2 - top}`;

		svg.style.width = `${width}px`;
		svg.style.height = `${height}px`;
		path.setAttribute("d", d);
	}
}
